import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '../Box';
import SvgIcon from '../SvgIcon';

import {
  DropdownStyled,
  DropTrigger,
  DropMenu,
  DropItem,
  IconStyled,
  Placeholder,
} from './Dropdown.styled';

export const dropdownSize = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  DEFAULT: 'default',
};

const PLACEMENT = {
  TOP: 'top',
  BOTTOM: 'bottom',
};

function getItemText(items, index, selectedItemPrefix) {
  if (items?.length) {
    const item = items[index];

    if (item) {
      if (selectedItemPrefix) return `${selectedItemPrefix} ${item.text}`;

      return (
        item.text || (item.props && item.props.data && item.props.data.text)
      );
    }
  }

  return '';
}

export default function Dropdown({
  items,
  trigger,
  icon,
  autoWidth,
  variant,
  iconRight,
  placement,
  placeholder,
  selectedItemPrefix,
  selectedItemIndex,
  boldSelectedItem,
  onItemSelected,
  showSelectedItem,
  size,
  dropTriggerStyle,
  placeholderStyle,
  dropItemStyle,
}) {
  const [isExpanded, setExpanded] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(selectedItemIndex);

  function itemSelected(item, index) {
    setExpanded(false);
    setSelectedIndex(index);
    if (onItemSelected) onItemSelected(item);
  }

  function generateItemComponents(itemList) {
    if (!Array.isArray(itemList)) return itemList;

    // TODO Think if there is a better way to validate input item format (object vs components)
    if (
      itemList.length &&
      itemList[0].value !== undefined &&
      itemList[0].text !== undefined
    ) {
      return itemList.map((item, index) => {
        const { key, text } = item;
        return (
          <DropItem
            key={key ? `${key}_${index}` : index}
            isActive={index === selectedIndex}
            onClick={() => itemSelected(item, index)}
            style={dropItemStyle}
          >
            {item.icon ? <Box mr={2}>{item.icon}</Box> : null}
            {text}
          </DropItem>
        );
      });
    }
    return itemList.map((item, index) => {
      const decoratedOnClick = selectedItem => {
        itemSelected(selectedItem, index);
        const originalOnClick = item.props && item.props.onClick;
        if (originalOnClick) originalOnClick(selectedItem);
      };
      return (
        <DropItem
          key={
            item?.props?.data?.key ? `${item.props.data.key}_${index}` : index
          }
          isActive={index === selectedIndex}
          onClick={() => decoratedOnClick(item.props.data)}
        >
          {item}
        </DropItem>
      );
    });
  }

  const listOfItems = useMemo(
    () => generateItemComponents(items),
    [items, selectedIndex],
  );
  const dropdownRef = useRef();

  function handleHideDropdown(event) {
    if (event.key === 'Escape') setExpanded(false);
  }

  function handleClick(event) {
    if (dropdownRef.current.contains(event.target)) return;

    setExpanded(false);
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleHideDropdown, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleHideDropdown, true);
    };
  });

  const placeholderText =
    (showSelectedItem &&
      getItemText(items, selectedIndex, selectedItemPrefix)) ||
    placeholder;

  const fullWidth = variant.includes('fullWidth');

  return (
    <DropdownStyled aria-label='Dropdown' ref={dropdownRef} size={size}>
      <DropTrigger
        onClick={() => setExpanded(!isExpanded)}
        aria-haspopup='listbox'
        aria-expanded={!!isExpanded}
        variant={variant}
        borderRadius={dropTriggerStyle?.borderRadius}
        style={dropTriggerStyle}
        hoverStyle={dropTriggerStyle?.hoverStyle}
      >
        {icon && !iconRight && <IconStyled name={icon} />}
        {trigger}
        {placeholderText && (
          <Placeholder
            style={placeholderStyle}
            boldSelectedItem={boldSelectedItem}
          >
            {placeholderText}
          </Placeholder>
        )}
        {iconRight ? (
          <SvgIcon
            icon={iconRight}
            sx={
              !fullWidth
                ? { marginLeft: 8 }
                : { position: 'absolute', right: 8 }
            }
          />
        ) : null}
      </DropTrigger>
      {isExpanded && (
        <DropMenu
          role='listbox'
          placement={placement}
          autoWidth={autoWidth}
          variant={variant}
        >
          {listOfItems}
        </DropMenu>
      )}
    </DropdownStyled>
  );
}

Dropdown.defaultProps = {
  icon: 'filter',
  selectedItemPrefix: '',
  placement: PLACEMENT.BOTTOM,
  size: dropdownSize.DEFAULT,
  showSelectedItem: true,
  selectedItemIndex: 0,
  boldSelectedItem: true,
  variant: '',
  dropItemStyle: {},
};

Dropdown.propTypes = {
  autoWidth: PropTypes.bool,
  placement: PropTypes.string,
  iconRight: PropTypes.element,
  variant: PropTypes.string,
  selectedItemPrefix: PropTypes.string,
  boldSelectedItem: PropTypes.bool,
  /**
   * An array of items to show as the options of the Dropdown. The array can be composed of plain objects or React
   * components. If plain objects are passed, each item needs to have <code>{ key, value, text }</code> properties.
   * <ul>
   *   <li><code>key</code>: it will be used as the <a href="https://reactjs.org/docs/lists-and-keys.html">key on the
   *   loop</a> used to paint all the options.</li>
   *   <li><code>value</code>: it can be an id useful to identify the item selected by the user</li>
   *   <li><code>text</code> will be the <code>String</code> that will be displayed on the item<li>
   * </ul>
   * <br/>
   * If React components are passed, any kind of component will be valid. Each component will be
   * wrapped inside a <code>li</code> component. Each React component passed in the items prop must have a
   * <code>data</code> prop containing <code>{ key, value, text }</code> properties as described above. If React
   * components are passed as items, it's also possible to define an <code>onClick</code> props for each of them.
   * This is a function that will be called immediately after the function passed as <code>onItemSelected</code> prop.
   */
  items: PropTypes.array.isRequired,
  /**
   * The trigger of the Dropdown component is the area that needs to be clicked to expand the list of options. This prop
   * can be either a <code>String</code> or a React component. In both cases, the prop will be rendered as it comes.
   */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  /**
   * This prop allows showing an <a href="https://storybook.wikifactory.com/?path=/docs/4--page">icon
   * present on the theme</a> by name. The Dropdown expects a <code>String</code> with the name of the icon and it will
   * paint it right before the trigger.
   */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * The placeholder is a <code>String</code> that will be shown after the trigger. It's possible to pass a <code>String</code> as a prop and the
   * Dropdown will render it. This element will have a different behavior depending on what is passed in the
   * showSelectedItem prop (read on).
   */
  placeholder: PropTypes.string,
  /**
   * This prop can be used to select a given item at render time.
   */
  selectedItemIndex: PropTypes.number,
  /**
   * A function that will be called when a given item is selected from the Dropdown. The function will receive the
   * <code>{ key, value, text }</code> corresponding to the selected item.
   */
  onItemSelected: PropTypes.func,
  /**
   * By default the Dropdown will render the text of the selected item in the placeholder. This prop can be passed as
   * false to avoid this behavior.
   */
  showSelectedItem: PropTypes.bool,
  /**
   * This prop allows to change the size of the component based on our <a href="https://storybook.wikifactory.com/?path=/docs/3--page">typography scale system</a>
   */
  size: PropTypes.oneOf(Object.values(dropdownSize)),
};
